import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import api from '../../services/api'
import toast from 'react-hot-toast'
import {
  CloudArrowUpIcon,
  XMarkIcon,
  DocumentIcon,
  PhotoIcon,
  VideoCameraIcon,
  CodeBracketIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'

const FILE_TYPE_ICONS = {
  image: PhotoIcon,
  video: VideoCameraIcon,
  document: DocumentIcon,
  code: CodeBracketIcon,
  other: DocumentIcon,
}

export default function FileUpload({ submissionId, onFilesChange }) {
  const [files, setFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({})

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    // Handle rejected files
    if (rejectedFiles.length > 0) {
      rejectedFiles.forEach(({ file, errors }) => {
        errors.forEach(error => {
          if (error.code === 'file-too-large') {
            toast.error(`${file.name} is too large. Maximum size is 50MB.`)
          } else if (error.code === 'file-invalid-type') {
            toast.error(`${file.name} is not a supported file type.`)
          } else {
            toast.error(`${file.name}: ${error.message}`)
          }
        })
      })
    }
    
    // Upload accepted files
    for (const file of acceptedFiles) {
      await uploadFile(file)
    }
  }, [submissionId])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: 50 * 1024 * 1024, // 50MB
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif'],
      'video/*': ['.mp4', '.mov', '.avi'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt', '.md'],
      'application/zip': ['.zip'],
      'text/x-python': ['.py'],
      'text/javascript': ['.js'],
      'text/html': ['.html'],
      'text/css': ['.css'],
      'application/json': ['.json'],
    }
  })

  const uploadFile = async (file) => {
    const formData = new FormData()
    formData.append('file', file)

    try {
      setUploading(true)
      setUploadProgress(prev => ({ ...prev, [file.name]: 0 }))

      const response = await api.post(
        `/submissions/${submissionId}/files/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            setUploadProgress(prev => ({ ...prev, [file.name]: percentCompleted }))
          },
        }
      )

      const uploadedFile = response.data.file
      setFiles(prev => [...prev, uploadedFile])
      onFilesChange && onFilesChange([...files, uploadedFile])
      toast.success(`${file.name} uploaded successfully!`)
      
      // Remove progress indicator after success
      setTimeout(() => {
        setUploadProgress(prev => {
          const newProgress = { ...prev }
          delete newProgress[file.name]
          return newProgress
        })
      }, 1000)
    } catch (error) {
      console.error('Upload error:', error)
      
      // Handle different error types
      if (error.response?.status === 422 && error.response?.data?.errors) {
        // Laravel validation errors
        const errorMessages = Object.values(error.response.data.errors).flat()
        errorMessages.forEach(msg => toast.error(msg))
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message)
      } else {
        toast.error(`Failed to upload ${file.name}`)
      }
      
      setUploadProgress(prev => {
        const newProgress = { ...prev }
        delete newProgress[file.name]
        return newProgress
      })
    } finally {
      setUploading(false)
    }
  }

  const deleteFile = async (fileId) => {
    try {
      await api.delete(`/files/${fileId}`)
      setFiles(prev => prev.filter(f => f.id !== fileId))
      onFilesChange && onFilesChange(files.filter(f => f.id !== fileId))
      toast.success('File deleted')
    } catch (error) {
      toast.error('Failed to delete file')
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-green-500 bg-green-50'
            : 'border-gray-300 hover:border-green-400 hover:bg-gray-50'
        }`}
      >
        <input {...getInputProps()} />
        <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        {isDragActive ? (
          <p className="text-green-600 font-medium">Drop files here...</p>
        ) : (
          <div>
            <p className="text-gray-700 font-medium mb-1">
              Drag & drop files here, or click to select
            </p>
            <p className="text-sm text-gray-500">
              Images, videos, documents, code files (max 50MB each)
            </p>
          </div>
        )}
      </div>

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="space-y-2">
          {Object.entries(uploadProgress).map(([filename, progress]) => (
            <div key={filename} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 truncate">
                  {filename}
                </span>
                <span className="text-sm text-gray-500">{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Uploaded Files List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-700">Uploaded Files</h4>
          {files.map((file) => {
            const Icon = FILE_TYPE_ICONS[file.type] || DocumentIcon
            return (
              <div
                key={file.id}
                className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3 hover:border-green-300 transition-colors"
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <Icon className="h-8 w-8 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">{file.size}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Download"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                  </a>
                  <button
                    onClick={() => deleteFile(file.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
